function parseDate(val) {
  if (val == null || val === "") return 0;
  const s = String(val).trim();
  const dmy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return new Date(Number(y), Number(m) - 1, Number(d)).getTime();
  }
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
}

function getJoinTimestamp(employee) {
  return parseDate(
    employee?.dateOfJoin || employee?.joiningDate || employee?.joinDate
  );
}

function getExitTimestamp(employee) {
  const raw =
    employee?.relievedDate ||
    employee?.dateOfLeaving ||
    employee?.resignationDate ||
    employee?.terminationDate ||
    employee?.lastWorkingDay;
  return parseDate(raw);
}

function normalizeGender(employee) {
  const g = String(employee?.user?.gender || employee?.gender || "")
    .trim()
    .toLowerCase();
  if (g === "m" || g === "male" || g === "man") return "male";
  if (g === "f" || g === "female" || g === "woman" || g === "women") return "female";
  return "other";
}

function isInactiveStatus(employee) {
  const s = String(employee?.employmentStatus || "").trim().toLowerCase();
  return ["relieved", "terminated", "resigned"].includes(s);
}

export function getPeriodBounds(periodMode, { month, year, rangeFrom, rangeTo }) {
  const now = new Date();
  const y = Number(year) || now.getFullYear();
  const m = Number(month) || now.getMonth() + 1;

  if (periodMode === "year") {
    return {
      start: new Date(y, 0, 1).getTime(),
      end: new Date(y, 11, 31, 23, 59, 59, 999).getTime(),
      label: `Year ${y}`,
    };
  }

  if (periodMode === "range") {
    const start = parseDate(rangeFrom);
    const endRaw = parseDate(rangeTo);
    const end = endRaw ? endRaw + 86400000 - 1 : start;
    return { start, end, label: "Custom range" };
  }

  const start = new Date(y, m - 1, 1).getTime();
  const end = new Date(y, m, 0, 23, 59, 59, 999).getTime();
  const monthName = new Date(y, m - 1, 1).toLocaleString("default", {
    month: "long",
  });
  return { start, end, label: `${monthName} ${y}` };
}

export function computeEmployeeDashboardMetrics(employees, periodOptions) {
  const rows = Array.isArray(employees) ? employees : [];
  const { start, end, label } = getPeriodBounds(
    periodOptions.periodMode,
    periodOptions
  );

  let male = 0;
  let female = 0;
  let otherGender = 0;
  let active = 0;
  let probation = 0;
  let confirmed = 0;
  let relieved = 0;
  let resigned = 0;
  let terminated = 0;
  let newJoinees = 0;
  let attrition = 0;
  let fullTime = 0;
  let external = 0;

  for (const emp of rows) {
    const gender = normalizeGender(emp);
    if (gender === "male") male += 1;
    else if (gender === "female") female += 1;
    else otherGender += 1;

    const status = String(emp?.employmentStatus || "").trim().toLowerCase();
    if (status === "probation") probation += 1;
    else if (status === "confirmed") confirmed += 1;
    else if (status === "relieved") relieved += 1;
    else if (status === "resigned") resigned += 1;
    else if (status === "terminated") terminated += 1;

    if (!isInactiveStatus(emp)) active += 1;

    const empType = String(emp?.employmentType || "").trim().toLowerCase();
    if (empType === "external") external += 1;
    else if (empType) fullTime += 1;

    const joinTs = getJoinTimestamp(emp);
    if (joinTs && joinTs >= start && joinTs <= end) newJoinees += 1;

    const exitTs = getExitTimestamp(emp);
    if (exitTs && exitTs >= start && exitTs <= end && isInactiveStatus(emp)) {
      attrition += 1;
    }
  }

  const headcount = rows.length;
  const genderKnown = male + female;
  const malePct = genderKnown ? Math.round((male / genderKnown) * 100) : 0;
  const femalePct = genderKnown ? Math.round((female / genderKnown) * 100) : 0;
  const avgBase = active > 0 ? active : headcount;
  const newJoineeRate = avgBase ? ((newJoinees / avgBase) * 100).toFixed(1) : "0.0";
  const attritionRate = avgBase ? ((attrition / avgBase) * 100).toFixed(1) : "0.0";

  return {
    periodLabel: label,
    headcount,
    active,
    male,
    female,
    otherGender,
    malePct,
    femalePct,
    genderRatio:
      female > 0 ? (male / female).toFixed(2) : male > 0 ? "—" : "0",
    newJoinees,
    newJoineeRate,
    attrition,
    attritionRate,
    probation,
    confirmed,
    relieved,
    resigned,
    terminated,
    fullTime,
    external,
  };
}
